import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { liveRegistrationUrl } from 'src/app/modules/auth/services/demo/registration-target';
import { DemoService } from 'src/app/modules/auth/services/demo/demo.service';
import { DemoExpiredDialogComponent } from 'src/app/modules/auth/components/demo-expired-dialog/demo-expired-dialog.component';
import { XdsViewportService } from 'src/app/shared/ui/a11y/viewport.service';
import {
  BANNER_COLLAPSE_KEY,
  BANNER_EXPAND_KEY,
  DEMO_BANNER_EXPANDED_KEY,
  dropLegacyBannerFlags,
  readBannerFlag,
  resolveBannerCollapsed,
  writeBannerFlag,
} from '../banner-collapse-state';

/**
 * Globaler "Demo-Modus"-Banner. Sichtbar, solange eine Demo-Session aktiv ist. Zeigt den 48-Stunden-
 * Restlauf (Std./Min.) + "Jetzt registrieren"-CTA. Wenn der Zugang abläuft, öffnet der Banner den
 * blockierenden {@link DemoExpiredDialogComponent}. Siehe 48h-Modell.
 */
@Component({
  selector: 'app-demo-banner',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      state('void', style({ transform: 'translateY(-100%)', opacity: 0 })),
      state('*', style({ transform: 'translateY(0)', opacity: 1 })),
      transition('void => *', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('* => void', animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  template: `
    <div
      *ngIf="isVisible"
      @slideDown
      class="demo-banner"
      [class.is-collapsed]="isCollapsed"
      role="status"
      aria-live="polite"
    >
      <div class="banner-content">
        <span class="banner-icon"><i class="fa-solid fa-flask"></i></span>
        <span class="banner-text">
          <span class="banner-title">{{ 'DEMO.BANNER_TITLE' | translate }}</span>
          <span class="banner-subtitle">{{ 'DEMO.BANNER_SUBTITLE' | translate }}</span>
          <span *ngIf="remainingUnit === 'hours'" class="banner-expiry"><span class="banner-sep" aria-hidden="true">· </span>{{ 'DEMO.BANNER_EXPIRES_HOURS' | translate:{ hours: remainingValue } }}</span>
          <span *ngIf="remainingUnit === 'minutes'" class="banner-expiry"><span class="banner-sep" aria-hidden="true">· </span>{{ 'DEMO.BANNER_EXPIRES_MINUTES' | translate:{ minutes: remainingValue } }}</span>
        </span>
        <a
          *ngIf="registerHref; else internalRegister"
          [href]="registerHref"
          class="banner-action"
        >
          {{ 'DEMO.BANNER_REGISTER' | translate }}
        </a>
        <ng-template #internalRegister>
          <a routerLink="/registration" class="banner-action">
            {{ 'DEMO.BANNER_REGISTER' | translate }}
          </a>
        </ng-template>
        <button
          *ngIf="showsCollapseToggle"
          type="button"
          class="banner-collapse-toggle"
          [attr.aria-label]="(isCollapsed ? expandKey : collapseKey) | translate"
          [attr.aria-expanded]="!isCollapsed"
          (click)="toggleCollapsed()"
        >
          <i
            class="fa-light"
            [class.fa-chevron-down]="isCollapsed"
            [class.fa-chevron-up]="!isCollapsed"
            aria-hidden="true"
          ></i>
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./demo-banner.component.css'],
})
export class DemoBannerComponent implements OnInit, OnDestroy {
  isVisible = false;
  /** Restlauf-Einheit für die i18n-Anzeige (Std./Min.) oder null (unbekannt). */
  remainingUnit: 'hours' | 'minutes' | null = null;
  remainingValue = 0;
  /** Absolute Cross-Domain-URL zur Live-Registrierung (nur im Demo-Build), sonst null. */
  registerHref: string | null = null;

  /**
   * Eingeklappt auf eine 36-px-Zeile (Countdown + „Registrieren" + Chevron).
   *
   * Nur auf der Telefonstufe, und dort der **Grundzustand**. Der volle Banner kostet
   * 80,6 px — 9,6 % des Bildschirms, dauerhaft; die Höhe entsteht aus `flex-wrap: wrap`
   * unter 480 px, nicht aus der Textmenge. Wer mehr wissen will, tippt den Chevron.
   */
  isCollapsed = false;

  readonly collapseKey = BANNER_COLLAPSE_KEY;
  readonly expandKey = BANNER_EXPAND_KEY;

  /** Die Bedienung erscheint nur, wo es etwas einzuklappen gibt. */
  get showsCollapseToggle(): boolean {
    return this.isVisible && this.viewport.deviceClass() === 'phone';
  }

  private expiryTimer?: ReturnType<typeof setTimeout>;
  private tickTimer?: ReturnType<typeof setInterval>;
  private expiredDialogShown = false;
  private readonly viewport = inject(XdsViewportService);

  constructor(
    private demoService: DemoService,
    private dialog: MatDialog,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isVisible = this.demoService.isDemoActive();
    if (!this.isVisible) {
      return;
    }

    this.registerHref = liveRegistrationUrl();

    if (this.demoService.isExpired()) {
      this.openExpiredDialog();
      return;
    }

    dropLegacyBannerFlags();
    this.restoreCollapsed();

    this.updateRemaining();
    // Anzeige jede Minute aktualisieren.
    this.zone.runOutsideAngular(() => {
      this.tickTimer = setInterval(() => this.zone.run(() => this.updateRemaining()), 60_000);
    });
    // Exakt beim Ablauf den Blocking-Dialog öffnen. setTimeout-Delay auf das JS-Limit klemmen
    // (> 2^31-1 ms würde SOFORT feuern); der Minuten-Tick fängt den Rest über isExpired ab.
    const MAX_DELAY = 2_000_000_000;
    const remaining = Math.min(this.demoService.getMillisRemaining(), MAX_DELAY);
    this.zone.runOutsideAngular(() => {
      this.expiryTimer = setTimeout(() => this.zone.run(() => this.openExpiredDialog()), remaining);
    });
  }

  ngOnDestroy(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
    }
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
    }
  }

  /**
   * Klappt von Hand auf oder zu.
   *
   * Gespeichert wird der **Ausklapp**-Wille, nicht der Einklapp-Zustand: eingeklappt ist
   * der Grundzustand und braucht keinen Eintrag. Wer aufklappt, will es für die laufende
   * Demo — auch nach einem Reload.
   */
  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
    writeBannerFlag(
      DEMO_BANNER_EXPANDED_KEY,
      this.isCollapsed ? null : this.sessionKey()
    );
    this.cdr.markForCheck();
  }

  /** Kennung der laufenden Demo-Sitzung — ihr Ablaufzeitpunkt. */
  private sessionKey(): string | null {
    return readBannerFlag(DemoService.LS_EXPIRES_AT);
  }

  private restoreCollapsed(): void {
    this.isCollapsed = resolveBannerCollapsed(
      readBannerFlag(DEMO_BANNER_EXPANDED_KEY),
      this.sessionKey(),
      this.viewport.deviceClass() === 'phone'
    );
  }

  private updateRemaining(): void {
    const ms = this.demoService.getMillisRemaining();
    if (ms <= 0) {
      this.remainingUnit = null;
      this.openExpiredDialog();
      return;
    }
    if (ms >= 3_600_000) {
      // Aufrunden: solange noch etwas von der Stunde übrig ist, "noch X Std." (nie weniger anzeigen).
      this.remainingUnit = 'hours';
      this.remainingValue = Math.ceil(ms / 3_600_000);
    } else {
      this.remainingUnit = 'minutes';
      this.remainingValue = Math.max(1, Math.ceil(ms / 60_000));
    }
    this.cdr.markForCheck();
  }

  private openExpiredDialog(): void {
    if (this.expiredDialogShown) {
      return;
    }
    this.expiredDialogShown = true;
    this.dialog.open(DemoExpiredDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
    });
  }
}
